// Explore page API client — search entities, top-referenced, entity documents.

import { getCsrfToken } from './api';
import type { IocLookupDocument } from './graphApi';

export interface ExploreSearchResult {
  value: string;
  category: string;
  sub_type?: string | null;
  doc_count: number;
  extra?: Record<string, unknown> | null;
  /** Neo4j elementId — set by /explore/search, absent in browse/top lists. */
  id?: string | null;
  /** Neo4j label the id belongs to (IOC, MalwareFamily, ThreatActor, …). */
  label?: string | null;
}

export interface FacetBucket {
  key: string;
  count: number;
}

/** A top-level clause of the parsed query, with its span in the query text. */
export interface QueryClause {
  label: string;
  kind: 'text' | 'filter' | 'not' | 'or';
  start: number;
  end: number;
}

export interface ExploreSearchResponse {
  results: ExploreSearchResult[];
  total: number;
  query: string;
  /** Entity-index search only: counts per category/group/country/verdict,
   *  ignoring the selected category tab. */
  facets?: Record<'category' | 'group' | 'country' | 'verdict', FacetBucket[]> | null;
  /** Parser hints, e.g. "Unknown field 'contry' (did you mean 'country'?)". */
  notices?: string[];
  clauses?: QueryClause[];
  engine?: 'es' | 'cypher';
}

export interface ExploreSearchParams {
  query: string;
  category?: string | null;
  sort_by?: string;
  limit?: number;
  offset?: number;
  country?: string | null;
  exclude_categories?: string[];
}

export interface Suggestion {
  kind: 'entity' | 'field' | 'value' | 'operator';
  label: string;
  detail?: string;
  count?: number | null;
  /** Replace query[start, end) with insert. */
  insert: string;
  start: number;
  end: number;
  entity?: ExploreSearchResult;
}

export interface SuggestResponse {
  context: { kind: string; start: number; end: number; field: string | null; partial: string };
  suggestions: Suggestion[];
  examples: { query: string; detail: string }[];
  engine: 'es' | 'cypher';
}

export interface TopReferencedCategory {
  category: string;
  items: ExploreSearchResult[];
}

export interface TopReferencedResponse {
  categories: TopReferencedCategory[];
  stats: Record<string, number>;
}

export interface EntityDocsParams {
  value: string;
  category: string;
  limit?: number;
  offset?: number;
}

export interface ExploreCategoryItem {
  key: string;
  label: string;
  count: number;
}

export interface ExploreCategoryGroup {
  key: string;
  label: string;
  categories: ExploreCategoryItem[];
}

export interface ExploreCategoriesResponse {
  groups: ExploreCategoryGroup[];
}

export interface EntityDocumentsResponse {
  value: string;
  category: string;
  documents: IocLookupDocument[];
  total: number;
}

export async function searchEntities(params: ExploreSearchParams): Promise<ExploreSearchResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/explore/search', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
    body: JSON.stringify({
      query: params.query || '',
      ...(params.category ? { category: params.category } : {}),
      ...(params.country ? { country: params.country } : {}),
      sort_by: params.sort_by || 'doc_count',
      limit: params.limit || 50,
      offset: params.offset || 0,
      ...(params.exclude_categories?.length ? { exclude_categories: params.exclude_categories } : {}),
    }),
  });

  if (!resp.ok) return { results: [], total: 0, query: params.query };
  return (await resp.json()) as ExploreSearchResponse;
}

/** Autocomplete for the token at `cursor` (fields, values, entities, operators). */
export async function suggestEntities(
  params: { query: string; cursor?: number; exclude_categories?: string[]; limit?: number },
  signal?: AbortSignal,
): Promise<SuggestResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/explore/suggest', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
    body: JSON.stringify({
      query: params.query,
      ...(params.cursor != null ? { cursor: params.cursor } : {}),
      ...(params.exclude_categories?.length ? { exclude_categories: params.exclude_categories } : {}),
      limit: params.limit ?? 8,
    }),
    signal,
  });
  if (!resp.ok) {
    return { context: { kind: 'next', start: 0, end: 0, field: null, partial: '' },
             suggestions: [], examples: [], engine: 'cypher' };
  }
  return (await resp.json()) as SuggestResponse;
}

export async function fetchExploreCategories(): Promise<ExploreCategoriesResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/explore/categories', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-CSRFToken': csrf },
  });
  if (!resp.ok) return { groups: [] };
  return (await resp.json()) as ExploreCategoriesResponse;
}

export interface TopReferencedParams {
  /** Category keys to build top lists for; omitted = all. */
  categories?: string[];
  /** false = corpus stats only (no per-category list queries). */
  lists?: boolean;
}

export async function fetchTopReferenced(params: TopReferencedParams = {}): Promise<TopReferencedResponse> {
  const csrf = await getCsrfToken();
  const qs = new URLSearchParams();
  if (params.lists === false) qs.set('lists', 'false');
  if (params.categories) qs.set('categories', params.categories.join(','));
  const query = qs.toString();
  const resp = await fetch(`/api/fastapi/explore/top-referenced${query ? `?${query}` : ''}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-CSRFToken': csrf },
  });

  if (!resp.ok) return { categories: [], stats: {} };
  return (await resp.json()) as TopReferencedResponse;
}

export async function fetchEntityDocuments(params: EntityDocsParams): Promise<EntityDocumentsResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/explore/entity-documents', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
    body: JSON.stringify({
      value: params.value,
      category: params.category,
      limit: params.limit || 50,
      offset: params.offset || 0,
    }),
  });

  if (!resp.ok) return { value: params.value, category: params.category, documents: [], total: 0 };
  return (await resp.json()) as EntityDocumentsResponse;
}
